// Request/response shapes for every endpoint in ARCHITECTURE §6 / server/README.md "API" that shared/types.ts does not
// already name. Checked against server/src (index.ts, do/user.ts) on 2026-11-01; candidates to move into shared/types.ts.
import type {
  AppState, CharId, ChatEvent, CheckIn, ClientKind, DoneFrom, FirstHelp, FirstStage, FirstWhere, LaterWhen, LocalNote, Message, Notify,
  OnboardingAnswers, ProposedCard, RegisterRequest, Reward, StuckHelp, StuckReason,
} from '@shared/types';

export type { ChatEvent, RegisterRequest };

/** Audio sent to the server: base64 (no data: prefix) + MIME type. HoldToTalk produces 16 kHz mono WAV. */
export interface AudioPayload { b64: string; mime: string }
/** Photo for chat: a downscaled JPEG (<= 1600 px). `thumb` (small data URL) is what history shows. */
export interface ImagePayload { b64: string; mime: string; thumb?: string }

export interface ApiErrorBody { error: { code: string; message: string } }

/**
 * Speech-to-text context. Real STT gets it as context for the transcription; the fake AI (AI_MODE=fake, staging)
 * returns a demo transcript per hint: onboarding (O8), notquite (O8 "Not quite" answer), change (O2), week (U3), plan,
 * chat, feedback (β sheet).
 */
export type SttHint = 'onboarding' | 'change' | 'week' | 'plan' | 'chat' | 'feedback' | 'notquite';

// ---------------------------------------------------------------- onboarding (no account yet)
export interface InviteCheckRequest { code: string }
export interface InviteCheckResponse { ok: boolean }

export type CantReason = 'place' | 'late' | 'need' | 'none';
interface FirstHelpBase { invite: string; ch: CharId; ob: OnboardingAnswers }
/** O8: what the user said (text, or audio for server-side STT). */
export interface FirstHelpAsk extends FirstHelpBase { text?: string; audio?: AudioPayload }
/** O8 "I can't do it right now" -> reason. `where`: their answer to "Where are you with it?", else the stage of the
 *  card on screen (new / middle / almost), so the step for now and tomorrow's card match it. */
export interface FirstHelpCant extends FirstHelpBase { reason: CantReason; thing: string; where?: FirstWhere }
/** O9 "Even smaller?": a smaller version of `card`. */
export interface FirstHelpSmaller extends FirstHelpBase { smaller: true; thing: string; card: string }
/** O8 "Not quite" (0.2.0): a new step instead of `card` (the card they turned down), from their answer to "Where are
 *  you with it?" (`where`, the newest one they tapped) and/or their own words (`fix`, everything they said in "Not
 *  quite" so far). `text` is what they first said. `turnedDown`: the cards turned down in earlier rounds, oldest first
 *  (none of them comes back either). */
export interface FirstHelpFix extends FirstHelpBase { text: string; thing: string; card: string; where?: FirstWhere; fix?: string; turnedDown?: string[] }
export type FirstHelpRequest = FirstHelpAsk | FirstHelpCant | FirstHelpSmaller | FirstHelpFix;
/**
 * FirstHelp plus `said` (the text used: typed, or the transcript when audio was sent). Note `prep.label` is the NOW
 * card's header ('NOW · 2 MIN'), not the done-card note (that is REASON_NOTE in content/onboarding.ts).
 */
export type FirstHelpResponse = FirstHelp & { said?: string };

export interface SttPublicRequest { invite: string; audio: AudioPayload; hint?: SttHint }
export interface SttRequest { audio: AudioPayload; hint?: SttHint }
export interface SttResponse { text: string }

export interface AuthResponse { token: string; state: AppState }
export interface LoginRequest { email: string; password: string }
export interface OkResponse { ok: boolean }

// ---------------------------------------------------------------- authenticated
export interface StateResponse { state: AppState }
export interface ChatRequest { text?: string; audio?: AudioPayload; image?: ImagePayload }
export interface DoneResponse { state: AppState; reward: Reward }
/** POST /api/cards/:id/done: `from` = finished from chat ('chat' ticked there, 'work-chat' Working together started
 *  there): the server adds the 'done' note to the chat. Leave it out from Today and notifications. */
export interface DoneRequest { from?: DoneFrom }
export interface StuckResponse { state: AppState; help: StuckHelp }
/** POST /api/cards/:id/stuck-chat: `messageId` = her ask (no reason) or her help (with a reason, then also `help`). */
export interface StuckChatResponse { state: AppState; messageId: string; help?: StuckHelp }
/** POST /api/stuck/:messageId: start/later -> `cardId` (later 'Tomorrow': tomorrow's carry card), split -> `cardIds`,
 *  again -> `messageId` (the new ask), close -> nothing else. */
export interface StuckActionResponse { state: AppState; cardId?: string; cardIds?: string[]; messageId?: string }
/** later: `cardId` is the carry card on tomorrow's list for 'Tomorrow', else the same card. */
export interface LaterResponse { state: AppState; cardId: string }
export interface SplitRequest { cards: { title: string; min: number }[] }
export interface SplitResponse { state: AppState; cardIds: string[] }
export interface UnsplitResponse { state: AppState; cardId: string }
/** nudge/accept: the new card. offer: the 2-minute card (already started: open W), null when declined. */
export interface CardIdResponse { state: AppState; cardId: string | null }
export interface StateOkResponse { state: AppState; ok: boolean }
export interface WeekAnswerRequest { text?: string; audio?: AudioPayload }
/** `text` = what was answered (the transcript when audio was sent). */
export interface WeekAnswerResponse { state: AppState; reply: string; text: string }
export interface WeekChestResponse { state: AppState; points: number }
/** `tz`: 'auto' (follow this device) or an IANA zone the user picked (no device changes it then). */
export interface SettingsPatch { planTime?: string; checkin?: CheckIn; reminders?: boolean; tz?: string }
/** PATCH /api/plan/:id: the corrected rows of a plan card still waiting for "Looks good". */
export interface PlanEditRequest { cards: { time: string; title: string; min: number }[] }
/** PATCH /api/cards/:id: `cardId` = the card changed (a carry card when the app still showed yesterday's). */
export interface CardEditResponse { state: AppState; cardId: string }
export interface DeviceRequest { platform: ClientKind; token: string }
export interface ScheduleResponse { items: LocalNote[] }
export interface MessagesResponse { messages: Message[] }
/** Notification buttons: morning "In 30 minutes" = snooze, follow-up "Not today" / "Accept". */
export type NotifyAction = 'snooze' | 'not-today' | 'accept';
export interface NotifyActionResponse { state: AppState; cardId?: string | null }
export type FriendAction = 'nudge' | 'cheer';
export type DebugTool = 'morning' | 'nudge' | 'away2' | 'chatonly' | 'evening' | 'crisis' | 'nextday' | 'restart' | 'points' | 'advance' | 'week';
/** points { add }, advance { minutes }, nextday { at?: '9:00 AM' }. */
export interface DebugBody { add?: number; minutes?: number; at?: string }
/** Every tool returns the state; chatonly adds the offer `message`, points adds unlock/levelFrom/levelTo. */
export interface DebugResponse { state: AppState; message?: Message; unlock?: unknown; levelFrom?: number; levelTo?: number }
/** debug/restart and DELETE /api/account: the account is gone (no state). */
export interface GoneResponse { ok: boolean; restart?: boolean }

// ---------------------------------------------------------------- admin events (ADMIN_P0 §2; names are the contract)
/** Where a card action was taken: Today, the chat (its card sheet, today's cards sheet, her stuck help), Working
 *  together, or the next-card strip (phone chat; the desk card and menu bar on desktop). Optional `from` in the body of
 *  start / later / remove / stuck-chat and of the stuck actions start / later; old servers ignore it. Not `done`'s
 *  `from` (DoneFrom), which is something else. */
export type ActionFrom = 'today' | 'chat' | 'cowork' | 'next';
/** POST /api/auth/register: the request plus the random id this device keeps (lib/analytics.ts deviceId()), so the
 *  server can link the onboarding steps sent before the account existed. */
export type RegisterBody = RegisterRequest & { device?: string };

/** App-reported events, POST /api/events (authenticated). Never chat text, card titles, memory or anything typed:
 *  `card` and `noteId` are ids. */
export interface AppEventData {
  /** The app came to the front: first time in this page ('launch'), again ('resume'), or brought up by a notification
   *  tap ('push', with its note id). */
  app_open: { from: 'launch' | 'resume' | 'push'; noteId?: string };
  /** The app went to the background after this many seconds in front. */
  app_bg: { secs: number };
  /** A notification was tapped (or one of its buttons): the server's note id and kind ('morning', 'card', ...). */
  push_open: { noteId: string; kind: string };
  /** A Working together session ended: the card id, the minutes on its timer, and how (Done, closed, Stuck). */
  cowork_end: { card: string; minutes: number; how: 'done' | 'close' | 'stuck' };
  /** Filming mode in Working together turned on / off. */
  film_mode: { on: boolean };
  /** The share card: opened, image saved, or shared. hideTask = "Hide what the card was" was on. */
  share_card: { variant: 'card' | 'streak' | 'week'; hideTask: boolean; action: 'open' | 'save' | 'share' };
}
export type AppEventKind = keyof AppEventData;
/** `at`: this device's clock (ms). */
export interface AppEvent<K extends AppEventKind = AppEventKind> { kind: K; at: number; data: AppEventData[K] }
/** POST /api/events: at most 50 per call; the server drops unknown kinds. */
export interface EventsRequest { events: AppEvent[] }

/** Onboarding steps, POST /api/onboarding/step (no account yet; gated like /api/onboarding/first-help). Data holds
 *  option labels and ids only, never the user's own words. */
export interface OnboardingStepData {
  o1_view: Record<string, never>;
  o1_ok: Record<string, never>;
  /** The O2 option picked, or 'own words' when their own words were the answer (the words are not sent). */
  o2: { change: string };
  o3: { stuck: string };
  o4: { tone: string[] };
  o5: { checkin: CheckIn };
  o6_ok: Record<string, never>;
  o6_change: Record<string, never>;
  /** `ch` picked; `best` = the buddy shown as BEST MATCH. */
  o7: { ch: CharId; best: CharId };
  /** A help card came in (the first one, or after "Not quite"): where the buddy thinks they are with it. */
  o8_card: { stage: FirstStage | null };
  o8_accept: Record<string, never>;
  o8_smaller: Record<string, never>;
  /** Their answer to "Where are you with it?": one of the three buttons, or 'words' (said or typed; not sent). */
  o8_notquite: { where: FirstWhere | 'words' };
  o8_cantnow: { reason: CantReason };
  o9_start: Record<string, never>;
  /** Minutes worked on the first card (one decimal). */
  o9_done: { minutes: number };
  o10_lit: Record<string, never>;
  o10_time: { planTime: string };
  o11_saved: Record<string, never>;
}
export type OnboardingStep = keyof OnboardingStepData;
export interface OnboardingStepRequest<S extends OnboardingStep = OnboardingStep> {
  invite: string; device: string; step: S; data: OnboardingStepData[S];
}

export type { LaterWhen, StuckReason, ProposedCard };

// ---------------------------------------------------------------- live (GET /api/live?token=…, WebSocket)
export type LiveServerMsg = { t: 'state'; state: AppState } | { t: 'notify'; note: Notify } | { t: 'ping' };
/** Plain-text 'ping' is also accepted (answered with 'pong' without waking the server). */
export type LiveClientMsg = { t: 'hello'; client: ClientKind } | { t: 'seen'; noteId: string } | { t: 'ping' };
/** Close codes: 4001 signed out (password reset / sign out everywhere), 4002 account deleted. */
export const LIVE_CLOSE = { signedOut: 4001, deleted: 4002 } as const;

// ---------------------------------------------------------------- admin (password ADMIN_PASSWORD; used by /admin)
export interface AdminLoginRequest { password: string }
export interface AdminLoginResponse { token: string }
export interface AdminUser {
  id: string; email: string; createdAt: string; invite: string; lastSeen: string | null; costUsd: number;
  ch: CharId | null; level: number | null; points: number | null; tempPassword: boolean;
}
export interface AdminInvite { code: string; maxUses: number; used: number; note: string | null; createdAt: string }
export interface AdminInvitesRequest { count: number; note: string; uses?: number }
export interface AdminCreateInvitesResponse { codes: string[] }
export interface TempPasswordResponse { tempPassword: string; email: string }

export type { FirstHelp };
